<template>
  <div style="margin-bottom:1em">
    <v-card>
      <v-input ref="nodeRef" class="math-editor-container" hide-details>
        <template v-slot:prepend>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" small outlined @click="addFunction()">
                <v-icon small left>fal fa-function</v-icon>
                Add Function
              </v-btn>
            </template>
            <span>Add Function</span>
          </v-tooltip>
        </template>
        <template v-slot:append>
          <v-dialog v-model="mathHelpDialog" width="600">
            <template v-slot:activator="{ on: dialogOn }">
              <v-tooltip top>
                <template v-slot:activator="{ on: tooltipOn }">
                  <v-btn v-on="{ ...dialogOn, ...tooltipOn }" small>
                    <v-icon color="primary">fas fa-question-circle</v-icon>
                  </v-btn>
                </template>
                <span>How to use the Math editor</span>
              </v-tooltip>
            </template>
            <v-card>
              <v-card-title>Math Editor Help</v-card-title>
              <v-card-text>
                <p>The math editor is a combination of simple text inputs and a math formula generator. By default, the editor has two text inputs and one function editor between. You can add more functions to the end by clicking the Add Function button, which will add a new function slot and a text input slot after it (any empty text boxes will be ignored when presenting the question to the student).</p>
                <p>Math formulas can be edited by clicking on the default f(x) that is put in as a placeholder. This will open a dialog where you can edit the formula using supported TeX formatting (you can see a full list of commands, along with what is supported and not supported, here: <a href="https://katex.org/docs/support_table.html" target="_blank">https://katex.org/docs/support_table.html</a>)</p>
              </v-card-text>
              <v-card-actions>
                <v-btn text @click="mathHelpDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
        <template v-for="(node, index) in nodes">
          <input
            v-if="node.type === 'text'"
            :key="'node-' + index"
            v-model="node.content"
            type="text"
            class="edit-node"
            v-autowidth="{ maxWidth: '600px', minWidth: '20px', comfortZone: 10 }" />
          <span v-else-if="node.type === 'function'" :key="'node-' + index" class="edit-node" @click="editNode(index)">
            <katex-element :expression="node.content" :display-mode="node.displayMode"></katex-element>
          </span>
        </template>
      </v-input>
    </v-card>
    <v-dialog v-model="editorOpen" width="400">
      <v-card>
        <v-card-title>Function Editor</v-card-title>
        <v-card-text>
          <v-textarea v-model="editingNode.content" :rows="2" label="Function (in TeX format)" outlined></v-textarea>
          <v-btn href="https://katex.org/docs/support_table.html" target="_blank">Functions/Input Format</v-btn>
          <v-switch v-model="editingNode.displayMode" :label="editingNode.displayMode ? 'Display as block (centered on its own line)' : 'Display inline'"></v-switch>
          <katex-element :expression="editingNode.content" :display-mode="editingNode.displayMode" :throw-on-error="false"></katex-element>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeEditor">Cancel</v-btn>
          <v-btn text color="success" @click="saveNode">Save Changes</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="editingIndex != null" text color="error" @click="removeNode">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<style>
@import '../../../node_modules/katex/dist/katex.min.css';
.math-editor-container input, .math-editor-containter span.edit-node {
  border: 1px solid #b5b5b59c;
  padding: 5px;
}
.math-editor-containter span.edit-node:hover {
  cursor: pointer;
  background-color: #b5b5b59c;
}
</style>
<script>
import { ref, watch, onMounted } from '@vue/composition-api'

export default {
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    mergeFields: {
      type: Array,
      default: () => ([])
    }
  },
  setup (props, { root, emit }) {
    const nodes = ref(Array.isArray(props.value) ? props.value : [])
    watch(() => props.value, () => {
      if (Array.isArray(props.value)) nodes.value = props.value
    })
    const nodeRef = ref(null)

    onMounted(() => {
      if (nodes.value.length === 0) {
        nodes.value.push({ type: 'text', content: '' })
        nodes.value.push({ type: 'function', content: 'f(x)', displayMode: false })
        nodes.value.push({ type: 'text', content: '' })
      }
    })

    const editorOpen = ref(false)
    const editingIndex = ref(null)
    const editingNode = ref({
      node: 'text',
      content: '',
      displayMode: false
    })

    function addFunction () {
      nodes.value.push({ type: 'function', content: 'f(x)', displayMode: false })
      editNode(nodes.value.length - 1)
      nodes.value.push({ type: 'text', content: 'Text' })
    }

    function addNode (node) {
      if (node.type === 'text') {
        nodes.value.push(node)
        root.$nextTick(() => {
          nodeRef.value.$el.children[1].children[0].children[nodes.value.length - 1].focus()
        })
      } else {
        editingNode.value = node
        editingIndex.value = null
        editorOpen.value = true
      }
    }

    function editNode (index) {
      editingIndex.value = index
      editingNode.value = JSON.parse(JSON.stringify(nodes.value[index]))
      editorOpen.value = true
    }
    function closeEditor () {
      editorOpen.value = false
      editingIndex.value = null
      editingNode.value = {
        node: 'text',
        content: '',
        displayMode: false
      }
    }
    function saveNode () {
      if (editingIndex.value != null) {
        nodes.value.splice(editingIndex.value, 1, editingNode.value)
      } else {
        nodes.value.push(editingNode.value)
      }
      closeEditor()
    }

    function removeNode () {
      nodes.value.splice(editingIndex.value, 1)
      closeEditor()
    }

    const mathHelpDialog = ref(false)

    return {
      nodes,
      nodeRef,
      editorOpen,
      editingIndex,
      editingNode,
      addFunction,
      addNode,
      editNode,
      closeEditor,
      saveNode,
      removeNode,
      mathHelpDialog
    }
  }
}
</script>
